import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, RumProvider } from "src/providers";
import { TutorialProvider } from "src/tutorials";
import { ROUTES } from "../constants";
import AppLayout from "./Layout";
import Dashboard from "./Dashboard";
import AllActivities from "./AllActivities";
import { MFRCreatePage } from "./MFR";
import { NetPPMCreatePage } from "./NetPPM";
import { RVMCreatePage } from "./RVMAnalysis";
import { WorkflowsPage, WorkflowRunPage } from "./Workflow";
import { CapabilitiesListPage, CapabilityEditPage } from "./Capability";
import { SkillManagementPage } from "./SkillManagement";
import { ProtectedRoute } from "./common/ProtectedRoute";
import ExperimentalRoute from "./common/ExperimentalRoute";
import { useAuthStore } from "../store/auth-store";
import { useFeatureFlagStore } from "../store/feature-flag-store";
import "../index.scss";
import GradientBackground from "./common/GradientBackground";

const App: React.FC = () => {
  const { initializeAuthorization } = useAuthStore();
  const { initializeFeatureFlags } = useFeatureFlagStore();

  // Initialize authorization once at app startup
  useEffect(() => {
    initializeAuthorization();
    initializeFeatureFlags();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- One-time initialization on mount

  return (
    <RumProvider>
      <BrowserRouter>
        <ThemeProvider>
        <TutorialProvider>
        <GradientBackground />
        <div style={{ position: "relative"}}>
          <AppLayout>
            <Routes>
              {/* Redirect root path to dashboard */}
              <Route
                path="/"
                element={<Navigate to={ROUTES.DASHBOARD} replace />}
              />

              {/* Dashboard route with layout */}
              <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />

              {/*/!* All Reports and Analysis route *!/*/}
              {/*<Route*/}
              {/*  path={ROUTES.REPORTS_ALL}*/}
              {/*  element={<AllReportsAndAnalysis />}*/}
              {/*/>*/}

              {/* All Activities route */}
              <Route path={ROUTES.ACTIVITIES_ALL} element={<AllActivities />} />

              {/* Capabilities routes */}
              <Route
                path={ROUTES.CAPABILITIES}
                element={<CapabilitiesListPage />}
              />
              <Route
                path={`${ROUTES.CAPABILITIES_EDIT}/:id`}
                element={<CapabilityEditPage />}
              />

              {/* Skill Management routes */}
              <Route path={ROUTES.SKILL_MANAGEMENT} element={<ExperimentalRoute><SkillManagementPage /></ExperimentalRoute>} />

              {/* MFR routes */}
              <Route path={ROUTES.MFR_CREATE} element={<ProtectedRoute workflowType="MFR"><MFRCreatePage /></ProtectedRoute>} />
              <Route
                path={ROUTES.MFR_VIEW}
                element={<ProtectedRoute workflowType="MFR"><WorkflowsPage tag="MFR" /></ProtectedRoute>}
              />
              <Route path={ROUTES.MFR_EDIT} element={<ProtectedRoute workflowType="MFR"><MFRCreatePage /></ProtectedRoute>} />
              <Route path={ROUTES.MFR_RUN} element={<ProtectedRoute workflowType="MFR"><WorkflowRunPage /></ProtectedRoute>} />
              <Route path={ROUTES.MFR_REPORT} element={<ProtectedRoute workflowType="MFR"><WorkflowRunPage /></ProtectedRoute>} />

              {/* NetPPM routes */}
              <Route
                path={ROUTES.NETPPM_CREATE}
                element={<ProtectedRoute workflowType="PPM"><NetPPMCreatePage /></ProtectedRoute>}
              />
              <Route
                path={ROUTES.NETPPM_VIEW}
                element={<ProtectedRoute workflowType="PPM"><WorkflowsPage tag="PPM" /></ProtectedRoute>}
              />
              <Route path={ROUTES.NETPPM_EDIT} element={<ProtectedRoute workflowType="PPM"><NetPPMCreatePage /></ProtectedRoute>} />
              <Route path={ROUTES.NETPPM_RUN} element={<ProtectedRoute workflowType="PPM"><WorkflowRunPage /></ProtectedRoute>} />
              <Route
                path={ROUTES.NETPPM_REPORT}
                element={<ProtectedRoute workflowType="PPM"><WorkflowRunPage /></ProtectedRoute>}
              />

              {/* RVM routes */}
              <Route
                path={ROUTES.RVM_VIEW}
                element={<ProtectedRoute workflowType="RVM"><WorkflowsPage tag="RVM" /></ProtectedRoute>}
              />

              {/* RVM Analysis route */}
              <Route path={ROUTES.RVM_CREATE} element={<ProtectedRoute workflowType="RVM"><RVMCreatePage /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} />} />
            </Routes>
          </AppLayout>
        </div>
        </TutorialProvider>
      </ThemeProvider>
    </BrowserRouter>
    </RumProvider>
  );
};

export default App;
